import { Component, type ReactNode } from 'react';
import { telemetry } from '../../services/telemetryService';
import { reloadOnceOnChunkLoadError } from '../../utils/chunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary — catches render errors and shows a recovery UI
 * instead of crashing to a blank white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A stale-bundle chunk miss (deploy window) heals with one reload —
    // skip the crash screen for something that self-repairs.
    if (reloadOnceOnChunkLoadError(error.message)) return;
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
    telemetry.trackError(error, { componentStack: info.componentStack ?? '' });
  }

  hasRecentBackup = (): boolean => {
    try {
      // Check for any localStorage key that looks like a survey backup
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('survey_backup_')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            // Check if backup was created within the last 60 seconds
            if (parsed.timestamp && (Date.now() - parsed.timestamp) < 60_000) {
              return true;
            }
          }
        }
      }
    } catch {
      // localStorage access can fail -- assume no backup
    }
    return false;
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <ErrorScreen
          message={this.state.error?.message}
          hasBackup={this.hasRecentBackup()}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Branded crash-recovery screen shared by the ErrorBoundary (React render
 * errors below the router) and RouteErrorElement (React Router route errors).
 * `hasBackup` switches the copy when a recent local survey backup exists.
 */
export function ErrorScreen({ message, hasBackup = false }: { message?: string; hasBackup?: boolean }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100vh', padding: '24px', fontFamily: 'var(--hts-font-body)', textAlign: 'center',
      backgroundColor: 'var(--hts-app-bg)', color: 'var(--hts-ink)',
    }}>
      <h2 style={{ marginBottom: '8px' }}>Something went wrong</h2>
      <p style={{ color: 'var(--hts-ink-secondary)', marginBottom: '24px', maxWidth: '500px' }}>
        An unexpected error occurred.{' '}
        {hasBackup
          ? 'Your most recent survey data was backed up locally and should be recoverable.'
          : 'Some recent changes may not have been saved.'}
        {' '}Try reloading the page or going back to the projects list.
      </p>
      {message && (
        <p style={{ color: 'var(--hts-ink-muted)', fontSize: '12px', marginBottom: '24px', fontFamily: 'var(--hts-font-mono)' }}>
          {message}
        </p>
      )}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 24px', fontSize: '14px', cursor: 'pointer',
            backgroundColor: 'var(--hts-primary-navy)', color: 'white', border: 'none', borderRadius: '4px',
          }}
        >
          Reload Page
        </button>
        <button
          onClick={() => { window.location.href = '/projects'; }}
          style={{
            padding: '10px 24px', fontSize: '14px', cursor: 'pointer',
            backgroundColor: 'var(--hts-surface)', color: 'var(--hts-ink)', border: '1px solid var(--hts-hairline)', borderRadius: '4px',
          }}
        >
          Back to Projects
        </button>
      </div>
    </div>
  );
}

export default ErrorBoundary;
